import React, { useState } from 'react'

import { useMutation } from '@apollo/client'
import { format } from 'date-fns'
import { Calendar, Users } from 'lucide-react'

import { CANCEL_RESERVATION } from 'Const'
import { useFocusReturn } from 'Hooks/useFocusReturn'
import { noty } from 'Utils'

import { ConfirmAction, DangerButton } from '../../../components/ConfirmAction'
import { Reservation } from '../types'
import { STATUS_COLOR_VAR, STATUS_LABEL, isCancellable } from '../utils'

interface ReservationCardProps {
  reservation: Reservation
}

export const ReservationCard: React.FC<ReservationCardProps> = ({ reservation }) => {
  const [cancel, { loading: cancelling }] = useMutation(CANCEL_RESERVATION)
  const [confirmCancel, setConfirmCancel] = useState(false)
  const cancelTriggerRef = useFocusReturn<HTMLButtonElement>(confirmCancel)

  const when = `${format(new Date(reservation.reservationAt), 'EEE, MMM d')} · ${format(
    new Date(reservation.reservationAt),
    'h:mm a',
  )}`
  const party = `${reservation.cover} ${reservation.cover === 1 ? 'guest' : 'guests'}`
  const canCancel = isCancellable(reservation)
  const statusLabel = STATUS_LABEL[reservation.status]
  const statusColor = `var(${STATUS_COLOR_VAR[reservation.status]})`

  const handleCancel = async () => {
    setConfirmCancel(false)
    try {
      await cancel({ variables: { id: reservation.id } })
    } catch (err) {
      noty(err instanceof Error ? err.message : 'Unable to cancel this reservation', 'error')
    }
  }

  return (
    <article className="user-profile-reservation-card">
      <div
        className="user-profile-reservation-card__frame"
        style={{ '--status-color': statusColor } as React.CSSProperties}
      >
        <div className="user-profile-reservation-card__band">
          <span className="user-profile-reservation-card__id">
            <Calendar aria-hidden size={18} strokeWidth={2} />
            {when}
          </span>
          <span className="user-profile-reservation-card__status-pill">{statusLabel}</span>
          <svg
            aria-hidden
            className="user-profile-reservation-card__wave"
            preserveAspectRatio="none"
            viewBox="0 0 200 16"
          >
            <path d="M0,9 C40,16 60,0 100,7 C140,14 160,2 200,8 L200,16 L0,16 Z" />
          </svg>
        </div>
        <div className="user-profile-reservation-card__face-body">
          <p className="user-profile-reservation-card__party">
            <Users aria-hidden size={14} strokeWidth={1.75} />
            {party}
          </p>
          {reservation.note ? <p className="user-profile-reservation-card__note">{reservation.note}</p> : null}
        </div>
        {canCancel ? (
          <div className="user-profile-reservation-card__footer">
            {confirmCancel ? (
              <ConfirmAction
                confirmLabel="Cancel"
                prompt="Cancel this reservation?"
                onCancel={() => setConfirmCancel(false)}
                onConfirm={() => void handleCancel()}
              />
            ) : (
              <DangerButton
                ref={cancelTriggerRef}
                disabled={cancelling}
                icon="cancel"
                onClick={() => setConfirmCancel(true)}
              >
                {cancelling ? 'Cancelling…' : 'Cancel'}
              </DangerButton>
            )}
          </div>
        ) : null}
      </div>
    </article>
  )
}
